import { CreatorProductState } from '@services/MarppleShopStudio/widget/CreatorProduct/core/store/types.ts';
import { validateSingleFieldByKey } from '@services/MarppleShopStudio/widget/CreatorProduct/core/store/helper.ts';
import {
  keyNameFormPathMap,
  ValidateField,
} from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/shared.ts';
import { MODE } from '@services/MarppleShopStudio/widget/CreatorProduct/core/types/types.ts';
import { CreatorProduct } from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/types.ts';
import { getStoresProductSchema } from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/store_product.ts';
import { Paths, setValueAtPathWithImmer, ValueAtPath } from '../../../../../../shared/type/ObjectPaths.ts';
import { z } from 'zod';

/**
 * 특정 필드의 값을 업데이트하는 상태 업데이트 함수(클로저)를 생성하는 고차 함수입니다.
 * 이 함수는 스토어 액션으로 등록되어 사용될 수 있습니다.
 *
 * @template K - 업데이트할 필드의 키 타입 (ValidateField).
 * @param keyName - 업데이트할 필드의 키 이름.
 * @param value - 해당 필드에 설정할 새로운 값 (타입은 unknown이며, 내부에서 검증 및 단언됨).
 * @returns 현재 상태(CreatorProductState<M>)를 받아 변경된 상태의 부분 집합을 반환하는 함수.
 */
export const setField_higherOrder =
  <K extends ValidateField>(
    keyName: K,
    // 이 value는 액션 호출 시 전달되는 값으로, 타입은 아직 알 수 없으므로 unknown.
    // 내부에서 현재 모드(M)에 맞는 구체적인 타입으로 단언하여 사용.
    value: unknown,
  ) =>
  /**
   * @template M - 현재 스토어의 MODE 타입.
   * @param state - 현재 스토어의 상태 객체 (읽기 전용으로 간주).
   * @returns 상태 변경이 필요한 부분(formData, errors)을 포함하는 객체.
   */
  <M extends MODE>(state: Readonly<CreatorProductState<M>>): Partial<CreatorProductState<M>> => {
    const actualPath = keyNameFormPathMap[keyName];

    const fieldError = validateSingleFieldByKey(
      state.mode,
      keyName,
      value as ValueAtPath<CreatorProduct<M>, typeof actualPath>,
    );

    let newFormData = state.formData;
    const newErrors = { ...state.errors };

    if (fieldError) {
      console.warn('setField_higherOrder 함수: ', fieldError);
      newErrors[keyName] = fieldError;
    } else {
      delete newErrors[keyName];

      const typedActualPath = actualPath as Paths<CreatorProduct<M>> & string;

      const typedValue = value as ValueAtPath<CreatorProduct<M>, typeof typedActualPath>;

      if (actualPath) {
        newFormData = setValueAtPathWithImmer(state.formData, typedActualPath, typedValue);
      } else {
        console.warn(
          `[setField_higherOrder] 경고: 키 "${String(keyName)}"에 대한 실제 경로 매핑을 찾을 수 없습니다. formData가 업데이트되지 않았습니다.`,
        );
      }
    }

    return {
      formData: newFormData,
      errors: newErrors,
    };
  };

export const validateForm =
  <T extends MODE>() =>
  (state: { mode: T; formData: CreatorProduct<T> }) => {
    const schema = getStoresProductSchema(state.mode);
    try {
      schema.parse(state.formData);
      return { errors: {} };
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errors: Record<string, string> = {};
        for (const err of error.errors) {
          const path = err.path.join('.');
          errors[path] = err.message;
        }
        return { errors };
      }
      return { errors: { unknown: '알 수 없는 오류가 발생했습니다.' } };
    }
  };
